import { Loader2, TriangleAlert } from 'lucide-react';
import { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { TRANSITIONAL_STATUSES } from '@/features/settings/config';
import { useProductFeature } from '@/providers/ProductFeaturesProvider';
import { PageLoader } from '@/routes';

interface FeatureGatedRouteProps {
  featureFlag: string;
  children: ReactNode;
  redirectTo?: string;
}

/**
 * What the product's status means for opening its application.
 *
 * `Degraded` deliberately opens. A degraded product is *serving* — the operator
 * only reaches that phase with every load-bearing component up — so blocking
 * would deny a working product. What is missing is a capability, which is the
 * application's business to describe, not this gate's.
 *
 * An absent status opens too. A product whose controller has not written status
 * yet is not a broken product, and gating on a missing value would block every
 * product that has no phase at all.
 */
function entryState(status: string | undefined): 'open' | 'starting' | 'failed' {
  if (!status) return 'open';
  if (TRANSITIONAL_STATUSES.includes(status)) return 'starting';
  if (status === 'Failed') return 'failed';
  return 'open';
}

function ProductNotReady({ status }: { status: string | undefined }) {
  const failed = status === 'Failed';
  return (
    <div
      data-testid="product-not-ready"
      className="flex h-full min-h-[60vh] flex-col items-center justify-center gap-3 p-8 text-center"
    >
      {failed ? (
        <TriangleAlert className="text-destructive h-8 w-8" />
      ) : (
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      )}
      <p className="text-sm font-medium">
        {failed ? 'This product failed to deploy.' : 'This product is still starting up.'}
      </p>
      <p className="text-muted-foreground max-w-sm text-sm">
        {failed
          ? 'Its last release did not come up. Settings → Version Upgrades shows what happened.'
          : 'Its components are still coming up. This page will be available once they are ready.'}
      </p>
    </div>
  );
}

/**
 * Gates a route on whether the product is switched on *and* whether it works.
 *
 * The second half is the one that was missing. The gateway comes up before the
 * worker, the sandbox and Redis do, so a product still being installed rendered
 * its full application: the user started something and it failed underneath,
 * naming a dependency they had never heard of.
 */
export function FeatureGatedRoute({
  featureFlag,
  children,
  redirectTo = '/',
}: FeatureGatedRouteProps) {
  const { enabled, isLoading, status } = useProductFeature(featureFlag);

  if (isLoading) {
    return <PageLoader />;
  }

  // Switched off wins over everything: there is no state to report.
  if (!enabled) {
    return <Navigate to={redirectTo} replace />;
  }

  if (entryState(status) !== 'open') {
    return <ProductNotReady status={status} />;
  }

  return <>{children}</>;
}
